// Represents a collection of DOM elements
export interface QueryCollection {
  remove: () => void;
  html: (newHtml?: string) => string | null;
  attr: (name: string, value?: string) => string | null | undefined;
  removeAttr: (name: string) => void;
  each: (callback: (index: number, element: any) => void) => void;
  append: (content: string) => void;
}

export interface QueryEngine {
  find: (selector: string) => QueryCollection;
  html: () => string | null;
}

/**
 * Converts MJML HTML to AMP-compliant HTML.
 *
 * It deletes quite a bit of stuff, but none of the deleted stuff seems to have been needed by AMP,
 * since it has more modern rendering than normal Gmail.
 */
export function mjmlHtmlToAmpHtml(queryEngine: QueryEngine): string {
  // Remove disallowed head elements
  queryEngine.find("head title").remove();
  queryEngine.find("head meta[http-equiv]").remove();
  queryEngine.find("head meta[name='viewport']").remove();

  // Extract all <style> content and combine into one
  let combinedStyles = "";
  queryEngine.find("head style").each((_, element) => {
    if (queryEngine.find(element).attr("amp4email-boilerplate") !== undefined) {
      return;
    }
    const styleContent = queryEngine.find(element).html() || "";
    combinedStyles += styleContent.trim() + "\n";
  });

  // Remove all existing <style> tags except for the boilerplate
  queryEngine.find("head style").each((_, element) => {
    if (queryEngine.find(element).attr("amp4email-boilerplate") === undefined) {
      queryEngine.find(element).remove();
    }
  });

  // Add a single <style amp-custom> with combined content
  queryEngine
    .find("head")
    .append(`<style amp-custom>${combinedStyles}</style>`);

  // Remove !important from all styles
  queryEngine.find("style").each((_, element) => {
    const styleContent = queryEngine.find(element).html() || "";
    queryEngine.find(element).html(styleContent.replace(/!important/g, ""));
  });

  // Remove <link> tags
  queryEngine.find("link").remove();

  // Remove disallowed style attributes
  queryEngine.find("style").each((_, element) => {
    const cleanedStyleContent = disallowedStyleAttributes.reduce(
      (styleContent, attr) =>
        styleContent.replace(new RegExp(`${attr}:.*?;`, "gi"), ""),
      queryEngine.find(element).html() || "",
    );

    // Update the style tag with the cleaned content
    queryEngine.find(element).html(cleanedStyleContent.trim());
  });

  // Remove @import statements
  queryEngine.find("style").each((_, element) => {
    const styleContent = queryEngine.find(element).html() || "";
    queryEngine.find(element).html(styleContent.replace(/@import.*?;/gi, ""));
  });

  // Remove disallowed styles with `::before` pseudo-selectors
  queryEngine.find("style").each((_, element) => {
    const styleContent = queryEngine.find(element).html() || "";

    // Properly handle nested media queries
    const cleanedStyleContent = styleContent
      .split(/(?<=\})/) // Split on closing brace while keeping the closing brace
      .map((rule) => rule.trim())
      .filter((rule) => rule && !rule.includes("::before"))
      .join("\n")
      .trim();

    // Update the style tag with the cleaned content
    queryEngine.find(element).html(cleanedStyleContent);
  });

  // Clean up <html> attributes
  queryEngine.find("html").removeAttr("xmlns");
  queryEngine.find("html").removeAttr("xmlns:o");
  queryEngine.find("html").removeAttr("xmlns:v");
  queryEngine.find("html").attr("amp4email", "");
  queryEngine.find("html").attr("data-css-strict", "");

  return queryEngine.html() ?? "";
}

const disallowedStyleAttributes = [
  "-webkit-text-size-adjust",
  "-ms-text-size-adjust",
  "-ms-interpolation-mode",
  "mso-table-lspace",
  "mso-table-rspace",
];
